// UserPoliciesView.jsx
import { Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import SearchInput from "../../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import { getUserPoliciesRows } from "./checkRowsUserData";

const UserPoliciesView = ({ entityData }) => {
  const { trackEvent } = useAPI();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();

  const [allPolicies, setAllPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  useEffect(() => {
    const policies = getUserPoliciesRows(entityData);
    setAllPolicies(policies);
  }, [entityData]);

  useEffect(() => {
    if (appliedFilters.searchString !== "") {
      const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
      const filteredRows = allPolicies.filter(
        (row) =>
          row?.policy_name?.toLowerCase().includes(lowerCaseSearchString) ||
          row?.policy_type?.toLowerCase().includes(lowerCaseSearchString) ||
          row?.source?.toLowerCase().includes(lowerCaseSearchString)
      );
      setFilteredPolicies(filteredRows);
    } else {
      setFilteredPolicies(allPolicies);
    }
  }, [appliedFilters.searchString, allPolicies]);

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for user policies - ${entityData?.display_name}`
    );
    const userName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "user";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`user_${userName}_policies`),
    };
    gridRef.current?.exportDataAsCsv(csvExportOptions);
  };

  const columnsToShow = [
    {
      field: "source",
      headerName: "Source",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <IntegrationImage
            name={params?.value}
            width={"24px"}
            style={{ margin: "16px" }}
            newLogo={true}
          />
        );
      },
    },
    {
      field: "policy_name",
      headerName: "Policy",
      flex: 1,
      renderCell: (params) => {
        return <TextIntegrationStyle title={""} value={params?.value} />;
      },
    },
    {
      field: "policy_type",
      headerName: "Type",
      flex: 1,
      renderCell: (params) => {
        return <TextIntegrationStyle title={""} value={params?.value} />;
      },
    },
  ];

  if (filteredPolicies.length === 0 && allPolicies.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "300px" }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredPolicies || filteredPolicies.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          apiRef={gridRef}
          columns={columnsToShow}
          rows={filteredPolicies}
        />
      </Box>
    </>
  );
};

export default UserPoliciesView;
